/**
 * 爆款模板 — mock 数据与开关
 * 后续对接真实接口时，整个文件删除即可。
 */
import type { TplTemplateVO } from '@/3.0/ai-viral-template/_components/template-ui/types';
import type { ViralTemplateFormApiResponse } from '@/3.0/ai-viral-template/utils/templateForm';

import type { ApiResponse } from '.';

/**
 * 模板模块数据源开关（统一在 service，UI 不感知）。
 * - `true`：走本地 mock
 * - `false`：走真实接口
 */
export const VIRAL_TEMPLATE_USE_MOCK = false;

/** mock 下「大健康」Tab 列表总条数上限 */
export const VIRAL_TEMPLATE_MOCK_HEALTH_TOTAL_CAP = 10;

export const TEMPLATE_VIDEO_URL =
  'https://assets.falcocut.ai/popular-templates/1774507577225-reo0s6.mp4';
export const TEMPLATE_POSTER_URL =
  'https://assets.falcocut.ai/popular-templates/1774508579810-c12tds.png';

const MOCK_CATEGORY_PATHS = ['/ugc/1', '/ugc/2', '/ugc/3', '/ugc/4', '/ads'] as const;

const MOCK_PRODUCT_IMAGE_URL =
  'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/popular-templates/1774682673390-km686l.png';

export const buildMockTemplates = (count = 72): TplTemplateVO[] => {
  const now = Date.now();
  return Array.from({ length: count }, (_, i) => {
    const id = i + 1;
    const category = MOCK_CATEGORY_PATHS[i % MOCK_CATEGORY_PATHS.length];
    return {
      id,
      name: `tpl-${category}-${i}`,
      description: '',
      category,
      preview: TEMPLATE_VIDEO_URL,
      cover: TEMPLATE_POSTER_URL,
      createTime: now - i * 1000,
      updateTime: now - i * 1000,
      modelFee: {
        amount: 10,
        basePrice: 10,
        discount: 100,
        modelCode: `MOCK_TMPL_${id}`,
        modelId: id,
      },
    };
  });
};

export const fetchViralTemplatePageMock = async (args: {
  category?: string;
  pageNum: number;
  pageSize: number;
  signal?: AbortSignal;
}): Promise<ApiResponse<TplTemplateVO>> => {
  if (args.signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  const { category, pageNum, pageSize } = args;
  const safePageSize = Math.max(1, Math.floor(Number(pageSize)) || 1);
  const safePageNum = Math.max(1, Math.floor(Number(pageNum)) || 1);

  const MIN_ITEMS_PER_TAB = 160;
  const MOCK_TEMPLATES = buildMockTemplates(
    MIN_ITEMS_PER_TAB * MOCK_CATEGORY_PATHS.length,
  );

  const filtered = category
    ? MOCK_TEMPLATES.filter((tpl) => tpl.category === category).sort(
        (a, b) => a.updateTime - b.updateTime,
      )
    : MOCK_TEMPLATES.sort((a, b) => a.updateTime - b.updateTime);

  const pool =
    category === '/ugc/3'
      ? filtered.slice(0, VIRAL_TEMPLATE_MOCK_HEALTH_TOTAL_CAP)
      : filtered;

  const start = (safePageNum - 1) * safePageSize;
  const items = pool.slice(start, start + safePageSize);

  return {
    code: 200,
    success: true,
    items,
    totalCount: pool.length,
    hasMore: start + safePageSize < pool.length,
    page: safePageNum,
    pageSize: safePageSize,
  };
};

export const fetchViralTemplateFormMock = async (args: {
  id: number;
}): Promise<ViralTemplateFormApiResponse> => {
  const { id } = args;
  return {
    success: true,
    code: 200,
    data: {
      templateId: id,
      name: `Mock Template Form ${id}`,
      cover: TEMPLATE_POSTER_URL,
      preview: TEMPLATE_VIDEO_URL,
      credit: 2,
      formSchema: {
        fields: [
          {
            key: 'product_image',
            label: 'b9013268a1c2f6aad6aae112d2caa844',
            type: 'image_upload',
            required: true,
            accept: 'image/*',
            minSizeMB: 0.1,
            maxSizeMB: 10,
            minWidth: 88,
            maxWidth: 4096,
            minHeight: 88,
            maxHeight: 4096,
            minRatio: '1:2',
            maxRatio: '2:1',
            placeholder: '',
            default: [MOCK_PRODUCT_IMAGE_URL],
            value: '',
          },
          {
            key: 'product_name',
            label: 'template-product-name',
            type: 'input',
            required: true,
            placeholder: '448ab9f640be205a35fd76bf83712d48',
            maxLength: 100,
            value: '',
          },
          {
            key: 'product_description',
            label: 'template-product-description',
            type: 'textarea',
            required: true,
            placeholder: '43d4d58b447fe55115229f217e4791d9',
            maxLength: 1500,
            value: '',
          },
        ],
      },
    },
  };
};

export const submitViralTemplateTaskMock =
  async (): Promise<ViralTemplateFormApiResponse> => {
    await new Promise((r) => setTimeout(r, 300));
    return { success: true, code: 200, message: 'ok' };
  };
